import styled from 'styled-components';
import { Button, Input, Select } from 'antd';

export const HomeContainer = styled.div`
  min-height: 100vh;
  background-image: url('/main-bg.jpg');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  position: relative;
  overflow-x: hidden;

  /* Compact shell: the page sits inside the layout's scroll box, which is already a
     tab bar shorter than the viewport — 100vh would leave a dead strip to scroll past.
     background-attachment: fixed also stutters badly on iOS Safari. */
  @media (max-width: 991px) {
    min-height: 100%;
    background-attachment: scroll;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(11, 14, 17, 0.7);
    z-index: 0;
  }
  
  > * {
    position: relative;
    z-index: 1;
  }
`;

export const MainContent = styled.div`
  flex: 1;
  padding: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 991px) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`;

export const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

export const PageTitle = styled.h1`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;

  @media (max-width: 991px) {
    font-size: 22px;
  }
`;

export const PageSubtitle = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.md};
  margin: 0;
`;

// Sidebar Styles
export const SidebarContainer = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 220px;
  height: 100vh;
  background: ${({ theme }) => theme.colors.background.secondary};
  border-right: 1px solid ${({ theme }) => theme.colors.border.primary};
  padding: ${({ theme }) => theme.spacing.lg};
  overflow-y: auto;
  z-index: 100;
  display: flex;
  flex-direction: column;

  /* Hide scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.secondary};
    border-radius: 3px;

    &:hover {
      background: ${({ theme }) => theme.colors.border.primary};
    }
  }
`;

export const SidebarHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  padding-bottom: ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.primary};
`;

export const SidebarTitle = styled.h2`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 12px;
  font-weight: 600;
  margin: 0;
`;

/** One flat sidebar destination: icon, label, optional trailing badge. */
export const SidebarNavItem = styled.button<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  margin-bottom: 2px;
  border: none;
  border-radius: 6px;
  background: ${({ $active }) => ($active ? 'rgba(14, 203, 129, 0.12)' : 'transparent')};
  color: ${({ $active, theme }) => ($active ? theme.colors.trading.buy : theme.colors.text.secondary)};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  text-align: left;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;

  &:hover {
    background: rgba(14, 203, 129, 0.08);
    color: ${({ theme }) => theme.colors.text.primary};
  }

  & > .nav-label {
    flex: 1;
  }
`;

export const SidebarSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

export const SectionTitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
  font-weight: 600;
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  align-items: center;
`;

export const SectionContent = styled.div`
  padding: ${({ theme }) => theme.spacing.sm} 0;
`;

export const BalanceCard = styled.div`
  background: ${({ theme }) => theme.colors.background.tertiary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 8px;
  padding: ${({ theme }) => theme.spacing.md};
`;

export const BalanceLabel = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

export const BalanceValue = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: 700;
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

export const BalanceChange = styled.div<{ positive: boolean }>`
  color: ${({ theme, positive }) =>
    positive ? theme.colors.trading.buy : theme.colors.trading.sell};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
`;

export const StatItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
`;

export const StatLabel = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 12px;
`;

export const StatValue = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 12px;
  font-weight: 600;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

// Marketplace Styles
export const MarketplaceHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

export const SearchFilterBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const SearchInput = styled(Input)`
  flex: 1;
  max-width: 400px;

  .ant-input {
    background: ${({ theme }) => theme.colors.background.secondary};
    border-color: ${({ theme }) => theme.colors.border.primary};
    color: ${({ theme }) => theme.colors.text.primary};

    &:hover {
      border-color: ${({ theme }) => theme.colors.primary};
    }

    &:focus {
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow: 0 0 0 2px rgba(14, 203, 129, 0.1);
    }
  }
`;

export const FilterSelect = styled(Select)`
  .ant-select-selector {
    background: ${({ theme }) => theme.colors.background.secondary} !important;
    border-color: ${({ theme }) => theme.colors.border.primary} !important;
    color: ${({ theme }) => theme.colors.text.primary} !important;

    &:hover {
      border-color: ${({ theme }) => theme.colors.primary} !important;
    }
  }

  .ant-select-focused .ant-select-selector {
    border-color: ${({ theme }) => theme.colors.primary} !important;
    box-shadow: 0 0 0 2px rgba(14, 203, 129, 0.1) !important;
  }
`;

export const StrategiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const StrategyCard = styled.div`
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 8px;
  padding: ${({ theme }) => theme.spacing.lg};
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100%;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    transform: translateY(-2px);
  }
`;

export const StrategyCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const StrategyIcon = styled.div`
  font-size: 40px;
  color: #0ECB81 !important;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  .anticon {
    color: #0ECB81 !important;
    font-size: 40px !important;
  }
`;

export const StrategyTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: 600;
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`;

export const StrategyDescription = styled.p`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  line-height: 1.6;
  flex: 1;
`;

export const StrategyFeatures = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const FeatureItem = styled.li`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.xs};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  padding-left: ${({ theme }) => theme.spacing.xs};
`;

export const StrategyFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border.primary};
`;

export const StrategyActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
`;

export const PrimaryButton = styled(Button)`
  background: ${({ theme }) => theme.colors.trading.buy} !important;
  border-color: ${({ theme }) => theme.colors.trading.buy} !important;
  color: #ffffff !important;
  font-weight: 600;

  &:hover:not(:disabled) {
    background: #0db870 !important;
    border-color: #0db870 !important;
    color: #ffffff !important;
  }
`;

export const StatusBadge = styled.span<{ color: string }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: 500;
  background: ${({ color }) => {
    switch (color) {
      case 'green':
        return 'rgba(14, 203, 129, 0.2)';
      case 'orange':
        return 'rgba(252, 213, 53, 0.2)';
      default:
        return 'rgba(132, 142, 156, 0.2)';
    }
  }};
  color: ${({ color }) => {
    switch (color) {
      case 'green':
        return '#0ECB81';
      case 'orange':
        return '#FCD535';
      default:
        return '#848E9C';
    }
  }};
`;

export const GithubLink = styled.a`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
  
  .anticon {
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 16px !important;
  }

  &:hover {
    color: #0db870 !important;
    
    .anticon {
      color: #0db870 !important;
    }
  }
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xl};
  min-height: 400px;
`;

export const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing.xl};
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
`;

